import React from 'react';
import { X, ExternalLink, AlertTriangle, CheckCircle2, Clock, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';

const TONE_STYLES = {
  ok:   { border: 'border-emerald-400/40', bg: 'bg-emerald-500/10', text: 'text-emerald-200', icon: CheckCircle2 },
  risk: { border: 'border-rose-400/50',   bg: 'bg-rose-500/10',    text: 'text-rose-200',    icon: AlertTriangle },
  info: { border: 'border-sky-400/40',    bg: 'bg-sky-500/10',     text: 'text-sky-200',     icon: TrendingUp },
};

/**
 * Overlay panel that Sentinel renders when a command completes.
 * Renders either "insight" cards (label + value) or "case" cards (case_number + candidate).
 */
export default function SentinelResponsePanel({ response, onClose, onNavigate }) {
  if (!response) return null;
  const { title, subtitle, cards = [], action, transcript, source, latency_ms } = response;

  const isInsight = cards.length > 0 && cards[0]?.label !== undefined;

  return (
    <div
      className="fixed bottom-32 right-6 z-[9998] w-[420px] max-w-[92vw] rounded-2xl border border-white/10 bg-slate-900/95 backdrop-blur-xl shadow-2xl text-slate-100 overflow-hidden"
      data-testid="sentinel-response-panel"
      style={{ animation: 'sentinelSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      <style>{`
        @keyframes sentinelSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-white/5">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/80 font-semibold mb-0.5">
            Sentinel · {source || 'command'}
          </div>
          <div className="text-base font-semibold truncate" data-testid="sentinel-response-title">{title}</div>
          {subtitle ? <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div> : null}
          {transcript ? (
            <div className="text-[11px] italic text-slate-500 mt-1">
              You said: <span className="text-slate-300">&ldquo;{transcript}&rdquo;</span>
            </div>
          ) : null}
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-white p-1 rounded-full hover:bg-white/5"
          data-testid="sentinel-response-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 max-h-[440px] overflow-y-auto">
        {cards.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">Nothing to show.</div>
        ) : isInsight ? (
          <div className="grid grid-cols-2 gap-3">
            {cards.map((c, i) => {
              const t = TONE_STYLES[c.tone || 'info'];
              const Icon = t.icon;
              return (
                <div
                  key={i}
                  className={`rounded-xl border ${t.border} ${t.bg} p-3`}
                  data-testid={`sentinel-insight-card-${i}`}
                >
                  <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider ${t.text} font-semibold mb-1`}>
                    <Icon className="w-3 h-3" /> {c.label}
                  </div>
                  <div className="text-2xl font-bold text-white leading-none">{c.value}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {cards.map((c, i) => {
              const t = TONE_STYLES[c.tone || 'info'];
              return (
                <button
                  key={c.id || i}
                  onClick={() => onNavigate?.(c.link)}
                  className={`w-full text-left rounded-lg border ${t.border} ${t.bg} px-3 py-2.5 hover:bg-white/5 transition-colors group`}
                  data-testid={`sentinel-case-card-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-mono text-slate-400 truncate">{c.case_number}</div>
                      <div className="text-sm font-medium text-white truncate">{c.candidate || '—'}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.sla_due_at ? (
                        <span className="flex items-center gap-1 text-[10px] text-slate-400">
                          <Clock className="w-3 h-3" /> {new Date(c.sla_due_at).toLocaleDateString()}
                        </span>
                      ) : null}
                      <span className={`text-[10px] font-medium uppercase px-1.5 py-0.5 rounded ${t.text} bg-white/5`}>
                        {(c.status || '').replace(/_/g, ' ')}
                      </span>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-white" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-white/5 bg-black/20">
        <span className="text-[10px] text-slate-500">
          {typeof latency_ms === 'number' ? `${latency_ms}ms` : ''}
        </span>
        {action?.url ? (
          <Button
            size="sm"
            variant="secondary"
            className="h-7 text-xs bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/25"
            onClick={() => onNavigate?.(action.url)}
            data-testid="sentinel-response-action"
          >
            {action.label}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
